<template>
  <div
    v-if="selectedAiReferrerProviders.length"
    class="ai-referrer-summary"
  >
    <div class="ai-referrer-summary__header">
      <div class="ai-referrer-summary__title">
        <i class="icon icon--peek-blue"></i>
        <span>{{ $t('common.aiReferrerSession') }}</span>
      </div>
    </div>
    <div class="ai-referrer-summary__body">
      <div
        v-for="provider in selectedAiReferrerProviders"
        :key="provider.key"
        class="ai-referrer-summary__row"
      >
        <i :class="['icon', `icon--ai-${provider.key}`]"></i>
        <div class="ai-referrer-summary__count">
          <i class="icon icon--ba-session-transparent"></i>
          <span>{{ provider.count }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

const PROVIDER_ORDER = [
  'openai_chatgpt',
  'gemini_google',
  'perplexity',
  'claude_anthropic'
]

const getProviderRank = providerKey => {
  const key = String(providerKey || '').toLowerCase()
  const orderIndex = PROVIDER_ORDER.indexOf(key)

  if (orderIndex > -1) return orderIndex
  if (key.endsWith('bot')) return PROVIDER_ORDER.length + 1

  return PROVIDER_ORDER.length
}

const sortByProviderOrder = providers => {
  return providers
    .map((provider, index) => ({ provider, index }))
    .sort((current, next) => {
      const currentDisabledRank = current.provider.count > 0 ? 0 : 1
      const nextDisabledRank = next.provider.count > 0 ? 0 : 1
      const currentRank = getProviderRank(current.provider.providerKey)
      const nextRank = getProviderRank(next.provider.providerKey)

      if (currentDisabledRank !== nextDisabledRank) return currentDisabledRank - nextDisabledRank
      if (currentRank !== nextRank) return currentRank - nextRank

      return current.index - next.index
    })
    .map(({ provider }) => provider)
}

export default {
  name: 'AiReferrerSummary',
  computed: {
    ...mapState([
      'journeyOptions',
      'journeyAiReferrerProviders'
    ]),
    selectedAiRefererIdSet () {
      return new Set((this.journeyOptions && this.journeyOptions.aiRefererIds) || [])
    },
    selectedAiReferrerProviders () {
      if (!this.selectedAiRefererIdSet.size) return []

      return sortByProviderOrder(this.journeyAiReferrerProviders)
        .map(provider => {
          const selectedValues = (provider.values || []).filter(value => this.selectedAiRefererIdSet.has(value.aiRefererId))
          return {
            key: provider.providerKey,
            name: provider.displayName,
            selectedCount: selectedValues.length,
            count: provider.count
          }
        })
        .filter(provider => provider.name && provider.selectedCount > 0)
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-referrer-summary {
  width: 204px;
  border: 1px solid #dedede;
  border-radius: 7px;
  background-color: #fff;
  box-shadow: 0 4px 8px -6px rgba(0, 0, 0, 0.22);
  box-sizing: border-box;
  overflow: hidden;
  pointer-events: auto;
  user-select: none;
  -webkit-user-select: none;

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 28px;
    padding: 4px 6px 4px 10px;
    color: #555;
    font-size: 12px;
    font-weight: 500;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;

    .icon {
      width: 20px;
      height: 20px;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    padding: 0 12px 7px;
  }

  &__row {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    height: 36px;
    box-sizing: border-box;
    user-select: none;
    &:not(&:last-child) {
      border-bottom: 1px dashed #ccc;
    }

    .icon {
      min-width: 0;
      background-size: cover;
    }
  }

  &__count {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    border-radius: 4px;
    color: #0e90a5;
    font-size: 11px;
    font-weight: 600;
    box-sizing: border-box;
    user-select: none;
  }
}
</style>
